import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { accessLocalStorage, getInternalStatistics } from '../../../services';

import PbxContext from '../../../context/PbxContext';
import '../../../libs/bulma.min.css';

import Loading from '../../../components/Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faArrowLeft, faSyncAlt, faUsers, faCog, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';

function InternalCallsStatistics() {
  const getItensStateGlobal = useContext(PbxContext);

  let url = window.location.href;
  url = url.split('/')[2];

  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const history = useHistory();

  const fetchStatistics = async () => {
    try {
      const response = await getInternalStatistics();
      if (response && response.success && response.data) {
        setStatistics(response.data);
      } else {
        setStatistics(null);
      }
      setLoading(false);
      setRefreshing(false);
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      setStatistics(null);
      setLoading(false);
      setRefreshing(false);
    }
  };

  const validateUserLogged = useCallback(async () => {
    const dataUser = await accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    if (dataUser && dataUser.user?.role !== 'root') return history.push('/home');
    await fetchStatistics();
  }, [history]);

  useEffect(() => {
    validateUserLogged();
  }, [validateUserLogged]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchStatistics();
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('pt-BR').format(num || 0);
  };

  const formatPercentage = (value, total) => {
    if (!total || total === 0) return '0%';
    return `${((value / total) * 100).toFixed(1)}%`;
  };

  if (loading) return <Loading />;

  return (
    <div className="container is-fluid">
      <div className="columns">
        <div className="column is-half is-offset-one-quarter has-text-centered">
          <strong className="is-size-3">ESTATÍSTICAS DE CHAMADAS INTERNAS</strong>
          <hr className="dropdown-divider"/>
        </div>
      </div>
      <div className="columns is-centered">
        <div className="column is-12">
          <div className="box">
            <div className="level">
              <div className="level-left">
                <div className="level-item">
                  <h1 className="title is-4">
                    <FontAwesomeIcon icon={faChartLine} className="mr-2" />
                    Estatísticas e Métricas
                  </h1>
                </div>
              </div>
              <div className="level-right">
                <div className="level-item">
                  <div className="buttons">
                    <button 
                      className={`button is-info ${refreshing ? 'is-loading' : ''}`}
                      onClick={handleRefresh}
                      disabled={refreshing}
                    >
                      <FontAwesomeIcon icon={faSyncAlt} className="mr-2" />
                      Atualizar
                    </button>
                    <Link to="/config/chamadas/internas" className="button is-light">
                      <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                      Voltar
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {!statistics ? (
              <div className="notification is-warning">
                <p className="has-text-centered">
                  <strong>Não foi possível carregar as estatísticas</strong>
                </p>
                <p className="has-text-centered">
                  Verifique se o serviço está funcionando corretamente ou tente novamente.
                </p>
              </div>
            ) : (
              <div>
                {/* Resumo Geral */}
                <div className="box">
                  <h2 className="title is-5">📊 Resumo Geral</h2>
                  <div className="columns">
                    <div className="column">
                      <div className="has-text-centered">
                        <p className="title is-1 has-text-primary">
                          <FontAwesomeIcon icon={faCog} />
                        </p>
                        <p className="title is-3">{formatNumber(statistics.total_configs || 0)}</p>
                        <p className="subtitle">Total de Configurações</p>
                      </div>
                    </div>
                    <div className="column">
                      <div className="has-text-centered">
                        <p className="title is-1 has-text-success">
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </p>
                        <p className="title is-3">{formatNumber(statistics.active_configs || 0)}</p>
                        <p className="subtitle">Configurações Ativas</p>
                      </div>
                    </div>
                    <div className="column">
                      <div className="has-text-centered">
                        <p className="title is-1 has-text-danger">
                          <FontAwesomeIcon icon={faTimesCircle} />
                        </p>
                        <p className="title is-3">{formatNumber(statistics.inactive_configs || 0)}</p>
                        <p className="subtitle">Configurações Inativas</p>
                      </div>
                    </div>
                    <div className="column">
                      <div className="has-text-centered">
                        <p className="title is-1 has-text-info">
                          <FontAwesomeIcon icon={faUsers} />
                        </p>
                        <p className="title is-3">{formatNumber(statistics.total_tenants || 0)}</p>
                        <p className="subtitle">Tenants Únicos</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Distribuição por Status */}
                <div className="columns">
                  <div className="column is-6">
                    <div className="box">
                      <h2 className="title is-5">📈 Distribuição por Status</h2>
                      <div className="content">
                        <div className="level">
                          <div className="level-left">
                            <span className="tag is-success">Ativas</span>
                          </div>
                          <div className="level-right">
                            <strong>{formatNumber(statistics.active_configs || 0)} ({formatPercentage(statistics.active_configs, statistics.total_configs)})</strong>
                          </div>
                        </div>
                        <progress 
                          className="progress is-success" 
                          value={statistics.active_configs || 0} 
                          max={statistics.total_configs || 1}
                        ></progress>

                        <div className="level">
                          <div className="level-left">
                            <span className="tag is-danger">Inativas</span>
                          </div>
                          <div className="level-right">
                            <strong>{formatNumber(statistics.inactive_configs || 0)} ({formatPercentage(statistics.inactive_configs, statistics.total_configs)})</strong>
                          </div>
                        </div>
                        <progress 
                          className="progress is-danger" 
                          value={statistics.inactive_configs || 0} 
                          max={statistics.total_configs || 1}
                        ></progress>
                      </div>
                    </div>
                  </div>

                  <div className="column is-6">
                    <div className="box">
                      <h2 className="title is-5">⚙️ Configurações por Tenant</h2>
                      <div className="content">
                        {statistics.tenants_breakdown && statistics.tenants_breakdown.length > 0 ? (
                          <table className="table is-fullwidth is-striped">
                            <thead>
                              <tr>
                                <th>Tenant</th>
                                <th>Configurações</th>
                                <th>Ativas</th>
                              </tr>
                            </thead>
                            <tbody>
                              {statistics.tenants_breakdown.map((tenant, index) => (
                                <tr key={index}>
                                  <td>
                                    <span className="tag is-link is-light">
                                      {tenant.tenant}
                                    </span>
                                  </td>
                                  <td>
                                    <span className="tag">
                                      {formatNumber(tenant.total)}
                                    </span>
                                  </td>
                                  <td>
                                    <span className="tag is-success">
                                      {formatNumber(tenant.active)}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="has-text-grey">Nenhum dado de tenant disponível</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detalhes Técnicos */}
                <div className="box">
                  <h2 className="title is-5">🔧 Detalhes Técnicos</h2>
                  <div className="columns">
                    <div className="column">
                      <div className="content">
                        <h3 className="subtitle is-6">Configurações de Prioridade</h3>
                        {statistics.priority_breakdown && statistics.priority_breakdown.length > 0 ? (
                          <table className="table is-narrow">
                            <thead>
                              <tr>
                                <th>Prioridade</th>
                                <th>Quantidade</th>
                              </tr>
                            </thead>
                            <tbody>
                              {statistics.priority_breakdown.map((priority, index) => (
                                <tr key={index}>
                                  <td>
                                    <span className="tag is-warning">
                                      {priority.priority}
                                    </span>
                                  </td>
                                  <td>{formatNumber(priority.count)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="has-text-grey">Nenhum dado de prioridade disponível</p>
                        )}
                      </div>
                    </div>

                    <div className="column">
                      <div className="content">
                        <h3 className="subtitle is-6">Configurações de Timeout</h3>
                        {statistics.timeout_breakdown && statistics.timeout_breakdown.length > 0 ? (
                          <table className="table is-narrow">
                            <thead>
                              <tr>
                                <th>Timeout (s)</th>
                                <th>Quantidade</th>
                              </tr>
                            </thead>
                            <tbody>
                              {statistics.timeout_breakdown.map((timeout, index) => (
                                <tr key={index}>
                                  <td>
                                    <span className="tag is-info">
                                      {timeout.timeout}s
                                    </span>
                                  </td>
                                  <td>{formatNumber(timeout.count)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="has-text-grey">Nenhum dado de timeout disponível</p>
                        )}
                      </div>
                    </div>

                    <div className="column">
                      <div className="content">
                        <h3 className="subtitle is-6">Últimas Atualizações</h3>
                        {statistics.recent_updates && statistics.recent_updates.length > 0 ? (
                          <div>
                            {statistics.recent_updates.slice(0, 5).map((update, index) => (
                              <div key={index} className="media">
                                <div className="media-content">
                                  <div className="content">
                                    <p>
                                      <small>
                                        <strong>#{update.id}</strong> - {update.tenant}
                                        <br/>
                                        <span className="has-text-grey">
                                          {new Date(update.updated_at).toLocaleString('pt-BR')}
                                        </span>
                                      </small>
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="has-text-grey">Nenhuma atualização recente</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Informações de Atualização */}
                <div className="notification is-light">
                  <p className="has-text-centered">
                    <small>
                      <strong>Última atualização:</strong> {new Date().toLocaleString('pt-BR')}
                      <br/>
                      As estatísticas são atualizadas em tempo real.
                    </small>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default InternalCallsStatistics;
